import { Suspense, lazy } from 'react';
import { Spinner, makeStyles, mergeClasses } from '@fluentui/react-components';
import { tokens, useShellTier } from '@vigeo/ui';
import type { LiveMapWithOutlinesProps } from './LiveMapWithOutlines.js';
import { anchorOf } from './liveMapView.js';

export type { LiveMapPlace, LiveMapProps } from './LiveMap.js';
export type { LiveMapOutlineLoader, LiveMapWithOutlinesProps } from './LiveMapWithOutlines.js';
export { LIVE_MAP_CREDIT, LIVE_MAP_CREDIT_LINK, liveMapLetter, liveMapLetters, type LiveMapOutline, type LiveMapPosition } from './liveMapView.js';

/**
 * The live map as the rest of the app uses it (#598). Leaflet and the map's own code are a separate file of the build
 * that the browser fetches only when a map is about to be drawn, so pages without a map do not carry them, and a roster
 * where nobody has a place never fetches them at all. What is fetched is the map with its outlines
 * (`LiveMapWithOutlines.tsx`): each place is drawn as a pin until its outline arrives from our own shipped files.
 */
const Loaded = lazy(() => import('./LiveMapWithOutlines.js'));

const useStyles = makeStyles({
  // The same box the map fills (`LiveMap.tsx`): all of the box it is given and never less than 300 high; 300 on a phone.
  // So nothing moves when the map arrives.
  waiting: {
    width: '100%', height: '100%', minHeight: '18.75rem', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge,
  },
  waitingTouch: { height: '18.75rem' },
});

export function LiveMap(props: LiveMapWithOutlinesProps) {
  const styles = useStyles();
  const touch = useShellTier() === 'mobile';
  if (!props.places.some((place) => anchorOf(place) !== null)) return null;
  return (
    <Suspense fallback={<div className={mergeClasses(styles.waiting, touch && styles.waitingTouch)} aria-busy="true"><Spinner size="small" label="Loading the map" /></div>}>
      <Loaded {...props} />
    </Suspense>
  );
}
